import { useState, useMemo } from 'react'
import axios from 'axios'
import { CurrencyInput } from './RateInput'
import {
    BTCIcon,
    DOGEIcon,
    USDTIcon,
    ETHIcon,
    USDCIcon,
    TRONIcon,
} from './CryptoIcons'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { toast } from 'sonner'
import {
    useInterestRates,
    useBulkUpdateInterestRates,
} from '@/hooks/useInterestRates'
import type { CryptoInterestRate } from '@/types/interest-rate'

const getErrorMessage = (error: unknown) => {
    if (axios.isAxiosError(error)) {
        return (
            (error.response?.data as { message?: string })?.message ||
            error.message
        )
    }
    return error instanceof Error
        ? error.message
        : 'An unexpected error occurred'
}

const getCryptoIcon = (symbol: string) => {
    switch (symbol.toUpperCase()) {
        case 'BTC':
            return <BTCIcon />
        case 'USDT':
            return <USDTIcon />
        case 'USDC':
            return <USDCIcon />
        case 'DOGE':
            return <DOGEIcon />
        case 'ETH':
            return <ETHIcon />
        case 'TRON':
            return <TRONIcon />
        default:
            return null
    }
}

interface LocalRates {
    [cryptoId: string]: {
        symbol: string
        name: string
        interestRatePercent: string
        lockedFundsInterestRatePercent: string
    }
}

const CRYPTO_ORDER = ['BTC', 'USDT', 'USDC', 'DOGE', 'ETH', 'TRON']

const toLocalRates = (rates: CryptoInterestRate[]): LocalRates => {
    const result: LocalRates = {}
    rates.forEach(r => {
        result[r.cryptoId] = {
            symbol: r.symbol,
            name: r.name,
            interestRatePercent: String(r.interestRatePercent),
            lockedFundsInterestRatePercent: String(
                r.lockedFundsInterestRatePercent
            ),
        }
    })
    return result
}

export const InterestRatesTab = () => {
    const { data: interestRates, isLoading, error } = useInterestRates()
    const bulkUpdateMutation = useBulkUpdateInterestRates()

    const [overrides, setOverrides] = useState<LocalRates>({})
    const [hasChanges, setHasChanges] = useState(false)

    // Derive editable rates: server data as base, user edits layered on top
    const localRates = useMemo<LocalRates>(() => {
        const base = interestRates ? toLocalRates(interestRates) : {}
        return { ...base, ...overrides }
    }, [interestRates, overrides])

    const sortedRates = useMemo(() => {
        return Object.entries(localRates).sort((a, b) => {
            const ia = CRYPTO_ORDER.indexOf(a[1].symbol.toUpperCase())
            const ib = CRYPTO_ORDER.indexOf(b[1].symbol.toUpperCase())
            return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib)
        })
    }, [localRates])

    const dailyRates = sortedRates
    const lockedRates = sortedRates

    const handleChange = (
        cryptoId: string,
        field: 'interestRatePercent' | 'lockedFundsInterestRatePercent',
        value: string
    ) => {
        setOverrides(prev => ({
            ...prev,
            [cryptoId]: {
                ...localRates[cryptoId],
                ...prev[cryptoId],
                [field]: value,
            },
        }))
        setHasChanges(true)
    }

    const handleSave = () => {
        const updates = Object.entries(localRates).map(([cryptoId, r]) => ({
            cryptoId,
            interestRatePercent: parseFloat(r.interestRatePercent) || 0,
            lockedFundsInterestRatePercent:
                parseFloat(r.lockedFundsInterestRatePercent) || 0,
        }))

        bulkUpdateMutation.mutate(
            { updates },
            {
                onSuccess: () => {
                    toast.success('Interest rates updated successfully')
                    setHasChanges(false)
                },
                onError: err => {
                    toast.error('Failed to update interest rates', {
                        description: getErrorMessage(err),
                    })
                },
            }
        )
    }

    if (isLoading) {
        return (
            <div className="space-y-8">
                <Skeleton className="h-8 w-full rounded-full" />
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {Array.from({ length: 6 }).map((_, i) => (
                        <Skeleton key={i} className="h-11 w-full" />
                    ))}
                </div>
            </div>
        )
    }

    if (error) {
        return (
            <div className="flex h-48 items-center justify-center">
                <p className="text-sm text-destructive">
                    Failed to load interest rates.
                </p>
            </div>
        )
    }

    const renderRateRow = (
        entries: typeof sortedRates,
        field: 'interestRatePercent' | 'lockedFundsInterestRatePercent'
    ) => (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {entries.map(([cryptoId, rate]) => (
                <div key={cryptoId} className="flex items-center gap-2">
                    <CurrencyInput
                        placeholder="0.00"
                        value={rate[field]}
                        onChange={e =>
                            handleChange(cryptoId, field, e.target.value)
                        }
                        showPercentageLeft
                    />
                    <div className="flex h-11 items-center gap-2 rounded-[12px] bg-secondary px-4">
                        {getCryptoIcon(rate.symbol)}
                        <span className="text-sm font-medium text-foreground">
                            {rate.symbol}
                        </span>
                    </div>
                </div>
            ))}
        </div>
    )

    return (
        <div className="space-y-8">
            {/* Daily Interest Rate Section */}
            <div className="space-y-4">
                <div className="flex h-8 items-center rounded-full bg-[#5FA8B4] px-6">
                    <h2 className="text-base font-medium text-white">
                        Daily interest rate
                    </h2>
                </div>
                <div className="space-y-3">
                    {renderRateRow(
                        dailyRates.slice(0, 3),
                        'interestRatePercent'
                    )}
                    {dailyRates.length > 3 &&
                        renderRateRow(
                            dailyRates.slice(3, 6),
                            'interestRatePercent'
                        )}
                </div>
            </div>

            {/* Locked Funds Interest Rate Section */}
            <div className="space-y-4">
                <div className="flex h-8 items-center rounded-full bg-[#5FA8B4] px-6">
                    <h2 className="text-base font-medium text-white">
                        Locked funds interest rate
                    </h2>
                </div>
                <div className="space-y-3">
                    {renderRateRow(
                        lockedRates.slice(0, 3),
                        'lockedFundsInterestRatePercent'
                    )}
                    {lockedRates.length > 3 &&
                        renderRateRow(
                            lockedRates.slice(3, 6),
                            'lockedFundsInterestRatePercent'
                        )}
                </div>
            </div>

            {hasChanges && (
                <div className="flex justify-start">
                    <Button
                        onClick={handleSave}
                        disabled={bulkUpdateMutation.isPending}
                        className="rounded-full bg-[#17A2B8] px-8 text-white hover:bg-[#1493a7]"
                    >
                        {bulkUpdateMutation.isPending
                            ? 'Saving...'
                            : 'Save changes'}
                    </Button>
                </div>
            )}
        </div>
    )
}
